import React, { useState } from "react";
import { Box, Button, Grid } from "@mui/material";
import useForm, { UseFormProps } from "../../../../hooks/useForm";
import _ from "lodash";
import FormElement, { FormElementPropsWithoutProduct } from "../FormElement";
import addressFields from "../../../../services/Product/FormDefinitions/modules/addressFields";
import caseFullFormStyle from "../Full/caseFullFormStyle";
import ApiClient from "../../../../services/ApiClient";
import { persistAddress, sanitizeOrganizationData } from "../../../../services/Persist/persisterService";
import { translate } from "../../../../services/Translations/translatorService";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonLoading from "../../../Button/ButtonLoading";
import {
  emailDefault,
  faxNumberDefault,
  isPhoneNumberWithoutSpecialCharactersDefault,
  requiredFieldDefault,
} from "../../../../services/validationRules";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import { Organization } from "../../../../types/Organization";
import { useSnackbar } from "notistack";
import { ProductUnion } from "../../../../types/ProductUnion";

type ProcessParticipantOrganizationFormProps = {
  product: ProductUnion;
  participant?: Organization;
  refreshPage: Function;
};

const ProcessParticipantOrganizationForm = ({
  product,
  participant,
  refreshPage,
}: ProcessParticipantOrganizationFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [buttonIsLoading, setButtonIsLoading] = useState(false);

  const formElements: FormElementPropsWithoutProduct[] = [
    {
      type: "ValidatorSelect",
      path: "participant.labelType",
      validators: requiredFieldDefault,
      options: () => [
        { value: "expert" },
        { value: "witness" },
        { value: "legalRepresentative" },
        { value: "judge" },
        { value: "interpreter" },
        { value: "insolvencyAdministrator" },
        { value: "tradeOffice" },
        { value: "other" },
      ],
    },
    {
      type: "ValidatorTextField",
      path: `participant.legalName`,
      validators: requiredFieldDefault,
    },
    {
      type: "ValidatorTextField",
      path: `participant.mainEmail.email`,
      validators: emailDefault,
    },
    {
      type: "ValidatorTextField",
      path: `participant.telephone`,
      validators: isPhoneNumberWithoutSpecialCharactersDefault,
    },
    {
      type: "ValidatorTextField",
      path: `participant.fax`,
      validators: faxNumberDefault,
      additionalProps: {
        InputProps: {
          startAdornment: <InputAdornment position="start">+49</InputAdornment>,
        },
      },
    },
    ...addressFields({ path: `participant.address` }),
  ];

  const deleteParticipant = async () => {
    setIsLoading(true);
    await ApiClient.delete("organizations/" + participant!.id);
    await refreshPage();
    setIsLoading(false);
  };

  const onSubmit: UseFormProps["onSubmit"] = async ({ values }) => {
    const participantData = _.merge({}, values.participant, {
      address: undefined,
      type: "processParticipant",
      caseId: product.id,
    });

    const sanitizedParticipantData = sanitizeOrganizationData(participantData);

    let createdParticipantOrganization;

    if (!participant) {
      setIsLoading(true);
      createdParticipantOrganization = await ApiClient.post("case_organizations", {
        body: JSON.stringify({ case: product["@id"], organization: sanitizedParticipantData }),
      });
      await persistAddress(createdParticipantOrganization.organization.address.id, values.participant.address);
    } else {
      setButtonIsLoading(true);
      await persistAddress(participant.address!.id, values.participant.address);
      await ApiClient.patch("organizations/" + participant.id, { body: JSON.stringify(sanitizedParticipantData) });
    }
    await refreshPage();
    if (createdParticipantOrganization) {
      enqueueSnackbar("Prozessbeteiligtes Unternehmen erfolgreich gespeichert", {
        variant: "custom",
        buttonType: "link",
        buttonLink: "#processParticipantOrganization" + createdParticipantOrganization.id,
        buttonText: "Zum Unternehmen",
        buttonTarget: "_self",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
        autoHideDuration: 10000,
      });
    }

    setIsLoading(false);
    setButtonIsLoading(false);
  };

  const initialValues = {
    participant: _.merge(
      {},
      participant || {
        labelType: "",
        legalName: "",
        mainEmail: {
          email: "",
        },
        telephone: "",
        fax: "",
        address: {
          streetAddress: "",
          postalCode: "",
          addressLocality: "",
        },
      },
    ),
  };

  const useFormProps = useForm({
    initialValues,
    onSubmit,
    identifier: product.id + "-" + (participant && participant.id),
    clearFormAfterSubmit: !participant,
    enableUnsavedChangesWarning: true,
  });

  const getLabel = (participant?: Organization) => {
    if (!participant) {
      return "Neues Unternehmen hinzufügen";
    }

    const labelType = translate("participant.labelType.values." + participant.labelType);
    return labelType + ": " + participant.legalName;
  };

  if (isLoading) {
    return (
      <Box sx={caseFullFormStyle.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container sx={caseFullFormStyle.grid}>
      <Grid size={12}>
        <Typography variant={"h4"} sx={{ mt: 1, mb: 1 }}>
          {getLabel(participant)}
        </Typography>
      </Grid>
      {_.map(formElements, (formElementProps) => {
        if (formElementProps.isHidden && formElementProps.isHidden({ values: useFormProps.values, product })) {
          return null;
        }

        const fullWidth = formElementProps.additionalProps && formElementProps.additionalProps.fullWidth;
        return (
          <Grid
            key={formElementProps.path}
            size={{
              xs: 12,
              md: !fullWidth && 6,
              lg: !fullWidth && 4,
            }}
          >
            <FormElement product={product} {...formElementProps} {...useFormProps} />
          </Grid>
        );
      })}
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      />
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        {participant && (
          <Button fullWidth onClick={deleteParticipant}>
            Entfernen
          </Button>
        )}
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4,
        }}
      >
        <ButtonLoading fullWidth variant="contained" onClick={useFormProps.handleSubmit} isLoading={buttonIsLoading}>
          Speichern
        </ButtonLoading>
      </Grid>
    </Grid>
  );
};
export default ProcessParticipantOrganizationForm;
